import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  type FacetedFilterOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { formatLongUnitAmount } from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import {
  RiCalendarLine,
  RiFilter3Line,
  RiPriceTagLine,
  RiWallet3Line,
} from "react-icons/ri";
import { z } from "zod";
import { CustodyAccountCard } from "~/components/Custody/CustodyAccountCard";
import { FundDistributionStatusBadge } from "~/components/FundDistribution/FundDistributionStatusBadge";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  fundDistributionStatusValues,
  fundDistributionsPageOrderValues,
} from "~/gql/client/enumValues";
import type {
  AdminFundDistributionRowFragment,
  FundDistributionsFilter,
  FundDistributionsPageOrder,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fund-distributions")({
  head: () => opsHead("Fund Distributions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminFundDistributionRowFragment & { id: string }>[] =
  [
    {
      id: "payDate",
      accessorFn: (row) => row.payDate,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Pay Date" />
      ),
      cell: ({ row }) => <DateUI date={row.original.payDate} />,
    },
    {
      id: "custodyAccount",
      accessorFn: (row) => row.custodyAccount.product?.internalName,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Custody Account" />
      ),
      cell: ({ row }) => (
        <div className="w-fit">
          <CustodyAccountCard
            custodyAccount={row.original.custodyAccount}
            link
          />
        </div>
      ),
    },
    {
      id: "period",
      accessorFn: (row) => row.periodStart,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Period" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <DateUI date={row.original.periodStart} />
          <span className="text-secondary">→</span>
          <DateUI date={row.original.periodEnd} />
        </div>
      ),
    },
    {
      id: "totalUnitAmount",
      accessorFn: (row) => row.totalUnitAmount,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Units"
          align="right"
        />
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatLongUnitAmount({ amount: row.original.totalUnitAmount })}
        </div>
      ),
    },
    {
      id: "status",
      accessorFn: (row) => row.status,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => (
        <FundDistributionStatusBadge status={row.original.status} />
      ),
    },
  ];

function View() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [filterAndSortState, setFilterAndSortState] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        custodyAccountIds: z.array(z.string()).optional().nullable(),
        statuses: z
          .array(z.enum(fundDistributionStatusValues))
          .optional()
          .nullable(),
        startDateTimeInclusive: z.date().optional().nullable(),
        sortOrder: z
          .enum(fundDistributionsPageOrderValues)
          .optional()
          .nullable(),
      }),
      defaultState: {
        sortOrder: "DATE_DESC",
      },
    });

  const { sortOrder, ...filter } = filterAndSortState;

  const baseInput = {
    first: 20,
    order: sortOrder,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    graphql(`
      query AdminFundDistributions(
        $after: String
        $first: Int!
        $order: FundDistributionsPageOrder
        $filter: FundDistributionsFilter!
      ) {
        fundDistributions(after: $after, first: $first, order: $order, filter: $filter) {
          pageInfo {
            endCursor
            hasNextPage
          }
          fundDistributions {
            ...AdminFundDistributionRow
          }
        }
      }

      fragment AdminFundDistributionRow on FundDistribution {
        id
        payDate
        periodStart
        periodEnd
        status
        totalUnitAmount
        custodyAccount {
          ...CustodyAccountCard
          product {
            id
            internalName
          }
        }
      }
    `),
    {
      variables: { after: null, ...baseInput },
      notifyOnNetworkStatusChange: true,
    },
  );

  const fundDistributions = data?.fundDistributions?.fundDistributions ?? [];
  const nextCursor = data?.fundDistributions?.pageInfo.hasNextPage
    ? (data.fundDistributions.pageInfo.endCursor ?? undefined)
    : undefined;

  const handleSortChange = (newSortingState: SortingState) => {
    const primarySort = newSortingState.at(0);
    let newSortOrder: FundDistributionsPageOrder = "DATE_DESC";
    if (primarySort?.id === "payDate") {
      newSortOrder = primarySort.desc ? "DATE_DESC" : "DATE_ASC";
    }
    if (newSortOrder !== sortOrder) {
      setFilterAndSortState({ ...filterAndSortState, sortOrder: newSortOrder });
    }
  };

  const { data: pageData } = useQuery(
    graphql(`
      query AdminFundDistributionsFilters {
        custodyAccounts {
          id
          shortRef
          currency
          product {
            __typename
            id
            internalName
          }
        }
      }
    `),
  );

  const custodyAccountOptions: FacetedFilterOption[] = (
    pageData?.custodyAccounts?.slice() ?? []
  )
    .filter((c) => c.product?.__typename === "FundProduct")
    .sort((a, b) =>
      (a.product?.internalName ?? "").localeCompare(
        b.product?.internalName ?? "",
      ),
    )
    .map((c) => ({
      value: c.id,
      searchValues: [c.product?.internalName ?? "", c.shortRef, c.currency],
      label: (
        <div className="flex flex-col">
          <span>{c.product?.internalName ?? "—"}</span>
          <span className="text-secondary text-xs">
            {c.shortRef} · {c.currency}
          </span>
        </div>
      ),
    }));

  return (
    <AdminPageContainer testId="admin-fund-distributions">
      <AdminPageHeader
        title="Fund Distributions"
        subtitle="Fund yield distributions and their per-asset allocations."
        buttons={
          <Button onClick={() => navigate({ to: "/fund-distributions/new" })}>
            New fund distribution
          </Button>
        }
      />
      <div>
        <DataTable
          data={fundDistributions}
          columns={columns}
          activeRowId={params.fundDistributionId}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualSorting={true}
          manualPagination={true}
          maxMultiSortColCount={1}
          hidePagination={true}
          pageSize={10_000}
          onRowClick={(row) => {
            const active = row.original.id === params.fundDistributionId;
            return active
              ? navigate({ to: "/fund-distributions" })
              : navigate({
                  to: "/fund-distributions/$fundDistributionId",
                  params: { fundDistributionId: row.original.id },
                });
          }}
          onSortingChange={handleSortChange}
          toolbarComponent={() => (
            <FilterBar
              filterValue={filterAndSortState}
              custodyAccountOptions={custodyAccountOptions}
              onFilterChange={(newFilter) =>
                setFilterAndSortState({ ...filterAndSortState, ...newFilter })
              }
            />
          )}
          className="overflow-hidden border"
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!fundDistributions.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}

function FilterBar({
  filterValue,
  onFilterChange,
  custodyAccountOptions,
}: {
  filterValue: FundDistributionsFilter;
  onFilterChange: (_: FundDistributionsFilter) => void;
  custodyAccountOptions: FacetedFilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");

  let filterCount = 0;
  if ((filterValue.custodyAccountIds?.length ?? 0) > 0) {
    filterCount++;
  }
  if ((filterValue.statuses?.length ?? 0) > 0) {
    filterCount++;
  }
  if (filterValue.startDateTimeInclusive) {
    filterCount++;
  }

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      custodyAccountIds: undefined,
      statuses: undefined,
      startDateTimeInclusive: undefined,
    });
  };

  const statusOptions = fundDistributionStatusValues.map((k) => ({
    value: k,
    label: <span>{k}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Pay date from</div>
                {filterValue.startDateTimeInclusive && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("statuses")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.statuses?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("custodyAccount")}
              >
                <RiWallet3Line />
                <div className="flex flex-1">Custody Account</div>
                {(filterValue.custodyAccountIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateTimeInclusive: value?.startAtInclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive:
                    filterValue.startDateTimeInclusive ?? undefined,
                  endAtExclusive: undefined,
                }}
              />
            )}
            {path === "statuses" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.statuses ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    statuses: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "custodyAccount" && (
              <ControlledFacetedFilterControl
                options={custodyAccountOptions}
                selectedValues={filterValue.custodyAccountIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    custodyAccountIds: newSelectedValues.length
                      ? newSelectedValues.filter((v) => v !== null)
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateTimeInclusive ?? undefined,
          endAtExclusive: undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.statuses ?? []}
        onClick={() => {
          setPath("statuses");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Custody Account"
        options={custodyAccountOptions}
        selectedValues={filterValue.custodyAccountIds ?? []}
        onClick={() => {
          setPath("custodyAccount");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
